import React, { useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import ResetPasswordModal from 'components/ResetPasswordModal/ResetPasswordModal';
import '../AuthModal/AuthModal.scss';

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
  </svg>
);

const EyeOnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

export default function AuthForm({ onSuccess, onSwitchToRegister }) {
  const { login, telegramLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const handleTelegramLogin = () => {
    const botId = process.env.REACT_APP_TELEGRAM_BOT_ID;
    if (!botId) {
      setError('Telegram вход не настроен');
      return;
    }

    const runAuth = () => {
      window.Telegram.Login.auth(
        { bot_id: botId, request_access: 'write' },
        async (data) => {
          if (!data) return;
          setLoading(true);
          setError(null);
          const result = await telegramLogin(data);
          setLoading(false);
          if (result.ok) {
            onSuccess && onSuccess();
          } else {
            const errMsg = result.error?.detail || result.error || 'Ошибка входа через Telegram';
            setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
          }
        }
      );
    };

    if (window.Telegram?.Login) {
      runAuth();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.onload = runAuth;
    script.onerror = () => setError('Не удалось загрузить Telegram виджет');
    document.head.appendChild(script);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await login({ email, password });
    setLoading(false);
    if (res.ok) {
      onSuccess && onSuccess();
    } else {
      const errMsg = res.error?.detail || res.error || 'Ошибка входа';
      setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="af-error">{error}</div>}
      
      <div className="af-group">
        <label className="af-label">Username или Email</label>
        <div className="af-field">
          <span className="af-icon"><MailIcon /></span>
          <span className="af-sep"></span>
          <input 
            className="af-input" 
            value={email} 
            onChange={(e) => setEmail(e.target.value)} 
            type="text"
            required 
            placeholder="Введите username или почту" 
          />
        </div>
      </div>

      <div className="af-group">
        <label className="af-label">Пароль</label>
        <div className="af-field">
          <span className="af-icon"><LockIcon /></span>
          <span className="af-sep"></span>
          <input 
            className="af-input" 
            value={password} 
            onChange={(e) => setPassword(e.target.value)} 
            type={showPassword ? 'text' : 'password'} 
            required 
            placeholder="Введите пароль" 
          />
          <button 
            type="button" 
            className="af-eye" 
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOffIcon /> : <EyeOnIcon />}
          </button>
        </div>
      </div>

      <div className="af-forgot">
        <a href="#forgot-password" onClick={(e) => { e.preventDefault(); setShowResetPassword(true); }}>
          Забыли пароль?
        </a>
      </div>

      <button className="af-submit-btn" type="submit" disabled={loading}>
        {loading ? 'Вход...' : 'Войти'}
      </button>

      <div className="af-social">
        <button 
          className="af-social-btn google" 
          type="button" 
          onClick={() => {
            const redirectUri = encodeURIComponent('https://ncore.kg/');
            const scope = encodeURIComponent('openid email profile');
            const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${redirectUri}&response_type=token&scope=${scope}&prompt=select_account`;
            window.location.href = authUrl;
          }}
        >
          <svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" fillRule="evenodd">
              <path d="M9 3.48c1.69 0 2.88.73 3.54 1.34l2.6-2.5C13.87.9 11.66 0 9 0 5.48 0 2.44 1.93 1 4.77l2.99 2.33C4.9 5.03 6.74 3.48 9 3.48z" fill="#EA4335"/>
              <path d="M17.64 9.2c0-.64-.06-1.12-.18-1.61H9v3.04h4.84c-.2 1.08-.82 2.08-1.74 2.72l2.8 2.17c1.63-1.5 2.54-3.72 2.54-6.32z" fill="#4285F4"/>
              <path d="M3.29 10.39A5.4 5.4 0 0 1 3 9c0-.62.11-1.22.29-1.79L.3 4.87A9 9 0 0 0 0 9c0 1.45.33 2.83.9 4.06l2.39-2.67z" fill="#FBBC05"/>
              <path d="M9 18c2.43 0 4.47-.8 5.96-2.17l-2.8-2.17C11.6 13.7 10.4 14.3 9 14.3c-2.26 0-4.1-1.55-4.73-3.64L1.28 12.98C2.72 15.81 5.76 18 9 18z" fill="#34A853"/>
            </g>
          </svg>
          Google
        </button>
        <button 
          className="af-social-btn telegram" 
          type="button" 
          onClick={handleTelegramLogin}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
            <g fill="none" fillRule="evenodd">
              <circle cx="120" cy="120" r="120" fill="#2AABEE"/>
              <path d="M52.4 121.4c40.4-16 66.5-26.4 79.3-31.2 9.1-3.4 17 2 13 12-2.4 5.9-18 39.9-28 61.9-3.5 8-6 14.6-10 18-3.5 3-8.5 4.5-14 3.5-9-1.6-21.5-7.2-33-13.5-8.8-5.2-12.5-9.6-13.5-13.5-1.9-6.9 1.4-15.3 11.2-18.9z" fill="#FFF"/>
            </g>
          </svg>
          Telegram
        </button>
      </div>

      <div className="af-footer">
        <span>Нет аккаунта?</span>
        <button 
          type="button" 
          className="af-footer-link" 
          onClick={onSwitchToRegister}
        >
          Регистрация
        </button>
      </div>

      <ResetPasswordModal 
        open={showResetPassword} 
        onClose={() => setShowResetPassword(false)} 
      />
    </form>
  );
}
